/*
 * Copyright 2026 Sony Corporation
 */

import { lookAtRotation, type DeviceOp, type Vec3 } from '../device-ops/schema';
import type { AgentTurn, RequestOps } from './agent';
import type { DeviceDigest } from '../device-ops/digest';

/**
 * A scripted stand-in for the device agent: canned ops for canned prompts.
 *
 * No model, no cost, no backend (devplan §12 U4). What it proposes is measured
 * against the scene's own extent rather than against constants, because that is
 * the thing the real agent will have to do and the thing a stub can get wrong
 * without anyone noticing until P4.
 *
 * It does not pretend. A prompt it does not know is answered by saying so.
 */

const DEFAULT_DELAY_MS = 700;

/** Where a device watching this scene stands: high enough to see over the subject. */
const eyeHeight = (digest: DeviceDigest): number => digest.bounds.center[1] + digest.bounds.radius * 0.35;

const place = (position: Vec3, target: Vec3, name: string): DeviceOp => ({
  op: 'add_device',
  name,
  position,
  look_at: target,
});

const ring = (count: number, radius: number, center: Vec3, height: number): Vec3[] =>
  Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    return [center[0] + Math.sin(angle) * radius, height, center[2] + Math.cos(angle) * radius];
  });

interface Script {
  /** Offered in the panel: what the stub knows is what the user is invited to ask. */
  prompt: string;
  matches: RegExp;
  reply: (digest: DeviceDigest) => AgentTurn<DeviceOp>;
}

const SCRIPTS: Script[] = [
  {
    prompt: 'Put twenty-four devices in a ring around the capture volume, all aimed at its centre',
    matches: /ring|circle|around/i,
    reply: (digest) => {
      const center = digest.bounds.center;
      const radius = Math.max(digest.bounds.radius * 0.9, 0.5);
      return {
        text: `Twenty-four devices on a ${radius.toFixed(1)} m circle at ${eyeHeight(digest).toFixed(1)} m, every one aimed at the centre of the volume.`,
        ops: ring(24, radius, center, eyeHeight(digest)).map((position, index) =>
          place(position, center, `Ring ${index + 1}`)
        ),
      };
    },
  },
  {
    prompt: 'Add four devices at the corners, aimed at the centre',
    matches: /corner|four|quad/i,
    reply: (digest) => {
      const center = digest.bounds.center;
      const reach = Math.max(digest.bounds.radius * 0.8, 0.5);
      const corners: Vec3[] = [
        [center[0] - reach, eyeHeight(digest), center[2] - reach],
        [center[0] + reach, eyeHeight(digest), center[2] - reach],
        [center[0] + reach, eyeHeight(digest), center[2] + reach],
        [center[0] - reach, eyeHeight(digest), center[2] + reach],
      ];
      return {
        text: 'Four devices, one to a corner, each looking in at the middle of the scene.',
        ops: corners.map((position, index) => place(position, center, `Corner ${index + 1}`)),
      };
    },
  },
  {
    // Moves only. Nothing is created, so this is the path that applies at once
    // — which is the contrast the UX review is meant to judge.
    prompt: 'Aim every device at the centre of the scene',
    matches: /\baim\b|look(?:ing)? at|\bface\b/i,
    reply: (digest) => {
      // A device that has never been placed has nothing to turn, so it is
      // counted out of the sentence as well as out of the ops.
      const ops = digest.devices.flatMap((device) =>
        device.position ? [{ op: 'look_at' as const, device_id: device.device_id, target: digest.bounds.center }] : []
      );
      return {
        text:
          ops.length === 0
            ? 'There are no placed devices in this world yet, so there is nothing to aim.'
            : `Turning ${ops.length} device${ops.length === 1 ? '' : 's'} to face the centre. Nothing moves position.`,
        ops,
      };
    },
  },
  {
    prompt: 'Raise every device to eye height',
    matches: /raise|height|higher|lower/i,
    reply: (digest) => ({
      text: `Lifting the devices to ${eyeHeight(digest).toFixed(1)} m, keeping where each one is aimed.`,
      ops: digest.devices.flatMap((device) => {
        if (!device.position) {
          return [];
        }
        const position: Vec3 = [device.position[0], eyeHeight(digest), device.position[2]];
        return [
          {
            op: 'set_pose' as const,
            device_id: device.device_id,
            position,
            // Re-aimed from the new height, or a raised device keeps looking
            // where it used to be standing.
            rotation: lookAtRotation(position, digest.bounds.center),
          },
        ];
      }),
    }),
  },
  {
    // A 70 mm end needs a zoom that reaches it, so the lens changes with it.
    prompt: 'Zoom every device to 70 mm',
    matches: /\bzoom\b|\d+\s*mm\b/i,
    reply: (digest) => ({
      text:
        digest.devices.length === 0
          ? 'There are no devices in this world yet, so there is nothing to zoom.'
          : `Fitting the FE 24-70mm F2.8 GM II to ${digest.devices.length === 1 ? 'the device' : `all ${digest.devices.length} devices`} and zooming to 70 mm.`,
      ops: digest.devices.map((device) => ({
        op: 'set_optics' as const,
        device_id: device.device_id,
        lens_id: 'sel2470gm2',
        focal_length_mm: 70,
      })),
    }),
  },
  {
    prompt: 'Stop every device down to F8',
    matches: /\bstop\b.*\bdown\b|\bf\s*\/?\s*\d/i,
    reply: (digest) => ({
      text:
        digest.devices.length === 0
          ? 'There are no devices in this world yet, so there is nothing to stop down.'
          : 'Stopping every lens down to F8 for more depth of field. Nothing moves.',
      ops: digest.devices.map((device) => ({ op: 'set_optics' as const, device_id: device.device_id, aperture_f: 8 })),
    }),
  },
];

export const CAMERA_STUB_PROMPTS: readonly string[] = SCRIPTS.map((script) => script.prompt);

const unknown: AgentTurn<DeviceOp> = {
  text: `This is a scripted stand-in, not a model — there is no AI behind it yet. It answers ${SCRIPTS.length} prompts:\n\n${CAMERA_STUB_PROMPTS.map(
    (prompt) => `• ${prompt}`
  ).join('\n')}`,
  ops: [],
};

export const createDeviceAgentStub =
  (delayMs: number = DEFAULT_DELAY_MS): RequestOps<DeviceOp, DeviceDigest> =>
  async (prompt, digest) => {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    const script = SCRIPTS.find(({ matches }) => matches.test(prompt));
    return script ? script.reply(digest) : unknown;
  };
